import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { OtaFooter } from '@ota/design-system';
import { PublicHeader } from '../components/PublicHeader';

// NOTE: contact details below are placeholders — confirm the real support
// email/phone/hours with the business before this page goes live.
const SUPPORT_EMAIL = 'otasupport@dataman.in';
const SUPPORT_PHONE = '+91-XXXXXXXXXX';
const SUPPORT_HOURS = 'Mon–Sat, 9:00 AM – 7:00 PM IST';

interface Faq {
  question: string;
  answer: string;
}

interface FaqSection {
  title: string;
  items: Faq[];
}

const FAQ_SECTIONS: FaqSection[] = [
  {
    title: 'Bookings & Reservations',
    items: [
      {
        question: 'How do I book a hotel?',
        answer:
          'Search for a city or property on the homepage, choose your check-in and check-out dates and the number of guests, then pick a room from the property page. You can complete checkout as a signed-in user or as a guest.',
      },
      {
        question: 'I didn’t get a booking confirmation. What should I do?',
        answer:
          'Confirmation emails can occasionally land in spam or take a few minutes to arrive. Check the "My Bookings" section of your account first — if the booking shows up there, it’s confirmed even if the email is delayed. If it doesn’t appear within 30 minutes of payment, contact support with your payment reference.',
      },
      {
        question: 'Can I book without creating an account?',
        answer:
          'Yes — you can check out as a guest with just your name, email, and mobile number. We’ll email you a secure link so you can view or manage that booking later without needing a password.',
      },
    ],
  },
  {
    title: 'Payments & Refunds',
    items: [
      {
        question: 'What payment methods are supported?',
        answer:
          'Payments are processed securely through Razorpay, supporting major debit/credit cards, UPI, net banking, and popular wallets. We never store your full card details on our servers.',
      },
      {
        question: 'My payment failed or is stuck as "pending" — will I be charged?',
        answer:
          'If a payment fails or is left incomplete, no booking is confirmed and any amount deducted is typically reversed by your bank/payment provider within 5–7 business days. Pending pay-now bookings that aren’t completed within 15 minutes are automatically released so the room isn’t held indefinitely.',
      },
      {
        question: 'How do refunds work?',
        answer:
          'Refund eligibility depends on the property’s cancellation policy, which is shown before you pay and again on your booking details page. Where a refund applies, you can view the estimated refund amount from "My Bookings" before confirming a cancellation. Approved refunds are credited back to your original payment method, usually within 5–10 business days depending on your bank.',
      },
    ],
  },
  {
    title: 'Cancellations & Modifications',
    items: [
      {
        question: 'How do I cancel a booking?',
        answer:
          'Go to "My Bookings", open the booking you want to cancel, and use the Cancel option. You’ll see the refund preview before confirming. Bookings for past dates can’t be cancelled or modified, since the stay has already lapsed.',
      },
      {
        question: 'Can I change my check-in/check-out dates or room after booking?',
        answer:
          'Modification options depend on the property and room availability. Where supported, you’ll find a Modify option on the booking’s detail page. If it’s not available online, contact support and we’ll check directly with the property.',
      },
    ],
  },
  {
    title: 'For Property Owners',
    items: [
      {
        question: 'How do I list my property on this platform?',
        answer:
          'Property owners can list their hotel or guest house through our dedicated property enlistment portal. You’ll need basic property details, room categories and pricing, photos, and your contact information to get started.',
      },
      {
        question: 'Where do I manage bookings and pricing for my property?',
        answer:
          'Once your property is approved, you can manage bookings, room inventory, and special rates from the hotelier dashboard using the credentials sent to your registered email.',
      },
    ],
  },
  {
    title: 'Account & Login',
    items: [
      {
        question: 'I’m not receiving my OTP for login.',
        answer:
          'OTPs are sent by SMS to the mobile number on your account and are valid for 15 minutes. Make sure the number is correct and has signal, then use "Resend OTP" if needed. If it still doesn’t arrive, try signing in with your email and password instead, or contact support.',
      },
      {
        question: 'Can I use the same mobile number for two accounts?',
        answer:
          'Each account should have its own unique mobile number and email address. If you believe your number is linked to the wrong account, contact support so we can look into it.',
      },
    ],
  },
];

export function Support() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />

      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6, md: 8 }, flex: 1, px: { xs: 2, sm: 3 } }}>
        {/* Hero */}
        <Stack spacing={1} sx={{ mb: { xs: 4, sm: 5 }, textAlign: 'center' }}>
          <Typography variant="overline" color="secondary.main">
            Support
          </Typography>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 700 }}>
            How can we help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
            Find quick answers below, or reach out to our team directly — we’re happy to help
            with bookings, payments, cancellations, and anything else.
          </Typography>
        </Stack>

        {/* Quick contact cards */}
        <Grid container spacing={2} sx={{ mb: { xs: 4, sm: 6 } }}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <EmailIcon color="secondary" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Email us
                </Typography>
                <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover" variant="body2">
                  {SUPPORT_EMAIL}
                </Link>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PhoneIcon color="secondary" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Call us
                </Typography>
                <Link href={`tel:${SUPPORT_PHONE}`} underline="hover" variant="body2">
                  {SUPPORT_PHONE}
                </Link>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <AccessTimeIcon color="secondary" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Support hours
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {SUPPORT_HOURS}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* FAQ */}
        <Stack spacing={4}>
          {FAQ_SECTIONS.map((section) => (
            <Box key={section.title}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                {section.title}
              </Typography>
              <Stack spacing={1}>
                {section.items.map((faq) => (
                  <Accordion key={faq.question} disableGutters elevation={0} variant="outlined">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary">
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: { xs: 4, sm: 6 } }} />

        {/* Still need help */}
        <Card elevation={2}>
          <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
            <WhatsAppIcon color="secondary" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Still need help?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Our team typically responds within one business day. Please include your booking
              reference (if you have one) so we can help you faster.
            </Typography>
            <Typography variant="body2">
              Write to us at{' '}
              <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover">
                {SUPPORT_EMAIL}
              </Link>{' '}
              or call{' '}
              <Link href={`tel:${SUPPORT_PHONE}`} underline="hover">
                {SUPPORT_PHONE}
              </Link>
              .
            </Typography>
          </CardContent>
        </Card>
      </Container>

      <OtaFooter />
    </Box>
  );
}
